import { getTranslatedProductName } from "@/app/directories/shop/helper";
import type { GetProductsByCategoryIdQuery } from "@frontend/lib/graphql/generated-gql";
import { cn } from "@frontend/shared/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLocale } from "next-intl";
import Link from "next/link";
import type { Dispatch, SetStateAction } from "react";
import type { NavbarBgColorType } from "./Navbar.types";

const getResultStyles = (bgMode: NavbarBgColorType) => {
  switch (bgMode) {
    case "black":
    case "transparent":
    case "custom":
      return {
        bg: "bg-[#555555] bg-opacity-25",
        text: "text-[#e5e5e5]",
        noResults: "text-[#e1e1e1]",
      };
    case "white":
      return {
        bg: "bg-black bg-opacity-5",
        text: "text-black",
        noResults: "text-gray-500",
      };
    default:
      return {
        bg: "bg-black bg-opacity-5",
        text: "text-black",
        noResults: "text-gray-500",
      };
  }
};

export const NavbarSearchResults = ({
  clientSlug,
  searchQuery,
  products,
  setIsSearch,
  bgMode,
  isBoutique = false,
}: {
  clientSlug: string;
  searchQuery: string;
  products: GetProductsByCategoryIdQuery["findManyProducts"];
  setIsSearch: Dispatch<SetStateAction<boolean>>;
  bgMode: NavbarBgColorType;
  isBoutique?: boolean;
}) => {
  const locale = useLocale();
  const languageCode = locale.replace("-", "_");

  // Don't want to accidentally not show the consumer something, so showing both translated and original name
  const searchResults = searchQuery
    ? Object.values(products).filter((product) => {
        const translatedName = getTranslatedProductName(
          product,
          languageCode,
        ).toLowerCase();
        const originalName = product.name.toLowerCase();
        const query = searchQuery.toLowerCase();
        return translatedName.includes(query) || originalName.includes(query);
      })
    : [];

  const styles = getResultStyles(bgMode);

  return (
    <div
      className={cn(
        "flex flex-col sm:gap-0 lg:gap-4 relative h-full w-full overflow-y-scroll pb-8",
      )}
    >
      {/* Show no results message when applicable */}
      {searchQuery && searchResults.length === 0 ? (
        <p
          className={cn(
            "detail lg:text-[20px] lg:mb-[20px] text-[#e1e1e1] font-light",
            styles.noResults,
          )}
        >
          No results found for "{searchQuery}"
        </p>
      ) : (
        <div className="search-results-container flex flex-col gap-3 ">
          <AnimatePresence mode="popLayout">
            {searchResults.map((result, i) => (
              <motion.div
                layout
                initial={{ opacity: 0, paddingLeft: "0px" }}
                animate={{
                  opacity: 1,
                  transition: {
                    ease: "easeInOut",
                    delay: 0.01 * i + 0.1,
                    layout: { duration: 0.3 }, // Optional: customize layout transition
                  },
                }}
                exit={{ opacity: 0 }}
                key={result.id}
              >
                <Link
                  key={result.id}
                  data-cy="product-item"
                  className={cn(
                    "flex rounded-[16px] sm:min-w-[calc(100%-32px)] p-4 justify-between",
                    styles.bg,
                    isBoutique && "bg-white bg-opacity-40",
                  )}
                  href={`/${clientSlug}/products/${result.id}`}
                  onClick={() => setIsSearch(false)}
                >
                  <span
                    className={cn(
                      "font-light text-white font-suisse w-[90%]",
                      styles.text,
                    )}
                  >
                    {getTranslatedProductName(result, languageCode)}
                  </span>

                  <ArrowRight
                    color={bgMode === "white" ? "#000000" : "#FFFFFF"}
                    strokeWidth={1}
                  />
                </Link>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};
